import { useCallback, useLayoutEffect, useRef, useState } from 'react'
import { useContainerWidth } from '@fs/zion-ui'

const computeColumns = (gridColumns, isSplitScreen) =>
  isSplitScreen ? Math.max(1, Math.floor(gridColumns / 2)) : gridColumns

/**
 * A hook to abstract away the logic of dynamically setting the column count
 * based upon screen size and split screen mode.
 *
 * The returned setter behaves like useState's setter with one addition: calling
 * it marks the value as "manual" so that subsequent resizes do not overwrite
 * the user's choice. The manual flag is cleared when isSplitScreen changes.
 * @returns {Array}
 */
export default function useColumnCount(isSplitScreen = false) {
  const atWidth = useContainerWidth()
  const gridColumns = atWidth({ default: 3, sm: 5, xl: 10 })
  const isManual = useRef(false)
  const [columnCount, setColumnCount] = useState(() => computeColumns(gridColumns, isSplitScreen))

  // Reset manual flag when split-screen mode changes so the layout re-syncs.
  useLayoutEffect(() => {
    isManual.current = false
  }, [isSplitScreen])

  useLayoutEffect(() => {
    if (!isManual.current) {
      setColumnCount(computeColumns(gridColumns, isSplitScreen))
    }
  }, [gridColumns, isSplitScreen])

  const setColumnCountManual = useCallback((value) => {
    isManual.current = true
    setColumnCount(value)
  }, [])

  return [columnCount, setColumnCountManual]
}
